import Phaser from "phaser";
import { AssetKeys } from "../../core/AssetKeys";
import { GameEvents } from "../../core/Events";
import { CharacterAnimator } from "../../animation/CharacterAnimator";
import { direction8FromVector, type EightDirection } from "../../animation/Direction";

export class Player extends Phaser.Physics.Arcade.Sprite {
  private readonly cursors: Phaser.Types.Input.Keyboard.CursorKeys;
  private readonly wasd: Record<"up" | "down" | "left" | "right", Phaser.Input.Keyboard.Key>;
  private readonly speed = 310;
  private readonly maxHealth = 1050;
  private readonly maxMana = 850;
  private health = this.maxHealth;
  private mana = this.maxMana;
  private readonly animator: CharacterAnimator;
  private facing: EightDirection = "s";
  private invulnerableUntil = 0;
  private shieldUntil = 0;
  private manaRegenerationCarry = 0;
  private shieldVisual?: Phaser.GameObjects.Arc;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y, AssetKeys.PlayerAtlas, "idle_s_000");

    scene.add.existing(this);
    scene.physics.add.existing(this);

    this.setCollideWorldBounds(true);
    this.setDepth(1000);
    this.setScale(0.8);
    this.animator = new CharacterAnimator(this, "player");

    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setSize(38, 52);
    body.setOffset(29, 35);

    this.cursors = scene.input.keyboard!.createCursorKeys();
    this.wasd = scene.input.keyboard!.addKeys({
      up: Phaser.Input.Keyboard.KeyCodes.W,
      down: Phaser.Input.Keyboard.KeyCodes.S,
      left: Phaser.Input.Keyboard.KeyCodes.A,
      right: Phaser.Input.Keyboard.KeyCodes.D,
    }) as Record<"up" | "down" | "left" | "right", Phaser.Input.Keyboard.Key>;

    scene.events.emit(GameEvents.PlayerHealthChanged, this.health, this.maxHealth);
    scene.events.emit(GameEvents.PlayerManaChanged, this.mana, this.maxMana);
  }

  override update(_time?: number, delta = this.scene.game.loop.delta): void {
    if (!this.active) return;

    const body = this.body as Phaser.Physics.Arcade.Body;
    const left = this.wasd.left.isDown || this.cursors.left.isDown;
    const right = this.wasd.right.isDown || this.cursors.right.isDown;
    const up = this.wasd.up.isDown || this.cursors.up.isDown;
    const down = this.wasd.down.isDown || this.cursors.down.isDown;

    const velocity = new Phaser.Math.Vector2(
      Number(right) - Number(left),
      Number(down) - Number(up),
    ).normalize().scale(this.speed);

    body.setVelocity(velocity.x, velocity.y);
    if (velocity.lengthSq() > 0) {
      this.facing = direction8FromVector(velocity.x, velocity.y, this.facing);
    }
    this.animator.updateLocomotion(this.facing, velocity.lengthSq() > 0, this.scene.time.now);
    this.setDepth(Math.round(this.y));

    this.manaRegenerationCarry += delta * 0.018;
    const points = Math.floor(this.manaRegenerationCarry);
    if (points > 0) {
      this.manaRegenerationCarry -= points;
      this.restoreMana(points);
    }

    if (this.shieldVisual) {
      this.shieldVisual.setPosition(this.x, this.y);
      if (this.scene.time.now >= this.shieldUntil) {
        this.shieldVisual.destroy();
        this.shieldVisual = undefined;
      }
    }
  }

  damage(amount: number): void {
    if (!this.active || this.scene.time.now < this.invulnerableUntil) return;

    const shielded = this.scene.time.now < this.shieldUntil;
    const finalAmount = shielded ? Math.ceil(amount * 0.25) : amount;
    this.health = Math.max(0, this.health - finalAmount);
    this.invulnerableUntil = this.scene.time.now + 350;
    this.setTintFill(0xffd4d4);
    this.scene.time.delayedCall(90, () => this.active && this.clearTint());

    this.scene.events.emit(GameEvents.PlayerHealthChanged, this.health, this.maxHealth);
    if (this.health <= 0) {
      (this.body as Phaser.Physics.Arcade.Body).setVelocity(0, 0);
      this.animator.playAction("death", 1500, this.facing);
      this.scene.events.emit(GameEvents.PlayerDefeated);
      this.setActive(false);
    }
  }

  heal(amount: number): number {
    const before = this.health;
    this.health = Math.min(this.maxHealth, this.health + amount);
    this.scene.events.emit(GameEvents.PlayerHealthChanged, this.health, this.maxHealth);
    return this.health - before;
  }

  spendMana(amount: number): boolean {
    if (!this.active || this.mana < amount) return false;
    this.mana -= amount;
    this.scene.events.emit(GameEvents.PlayerManaChanged, this.mana, this.maxMana);
    return true;
  }

  activateShield(durationMs: number): void {
    this.shieldUntil = Math.max(this.shieldUntil, this.scene.time.now + durationMs);
    if (!this.shieldVisual) {
      this.shieldVisual = this.scene.add.circle(this.x, this.y, 54, 0x884fd2, 0.08)
        .setStrokeStyle(3, 0xb68cff, 0.72)
        .setDepth(this.depth + 1);
    }
    this.scene.tweens.add({
      targets: this.shieldVisual,
      alpha: { from: 0.35, to: 0.8 },
      duration: 450,
      yoyo: true,
      repeat: -1,
    });
  }

  teleportTo(worldX: number, worldY: number): void {
    const x = Phaser.Math.Clamp(worldX, 80, 1840);
    const y = Phaser.Math.Clamp(worldY, 150, 1000);
    this.setPosition(x, y);
    this.invulnerableUntil = this.scene.time.now + 450;
    this.setAlpha(0.35);
    this.scene.tweens.add({ targets: this, alpha: 1, duration: 220 });
  }

  getFacing(): EightDirection {
    return this.facing;
  }

  private restoreMana(amount: number): void {
    if (this.mana >= this.maxMana) return;
    this.mana = Math.min(this.maxMana, this.mana + amount);
    this.scene.events.emit(GameEvents.PlayerManaChanged, this.mana, this.maxMana);
  }
}
